/**
 * Load the Leads API SDK on demand.
 *
 * The SDK is a plain <script> (REACT_APP_LEADS_SDK_SCRIPT_SRC, which carries the
 * channel id in its path) that publishes window._elnkLeadsSDK once its async init
 * has finished. Script onload fires before that, so readiness is the SDK being
 * published, not the load event.
 *
 * Kept private. The checkout's other leads?.x?.() call sites read that global
 * through useLeadsSDK and have been inert since ELNKWEB-2590 removed the app-wide
 * loader; loading the SDK for this form must not quietly switch that tracking back
 * on. So before injecting, a permanent accessor trap is installed on the global:
 * the SDK only ever assigns it, never reads it, and every assignment from then on
 * (ours, or any other copy's) is captured here and never lands on window. A copy
 * that published before the trap existed is on window already and is used as is.
 *
 * At most one copy of ours ever runs. A timed-out tag is left in place, because
 * removing an async script does not stop it executing once fetched -- and a copy
 * that runs with its tag gone cannot read its channel id from it. The next call
 * just waits for it again. Only a tag that errored is removed and re-injected.
 */
const SDK_GLOBAL = '_elnkLeadsSDK';
const SCRIPT_ATTR = 'data-elnk-leads-sdk';
const SDK_SCRIPT_SELECTOR = 'script[src*="/scripts/leads/"][src*="/sdk.min.js"]';
const POLL_INTERVAL_MS = 100;
const READY_TIMEOUT_MS = 15000;

let pending = null;
let privateSDK = null;
let injected = null;
// Fails whichever wait is current, so a late onerror is not left to the deadline.
let failPending = null;
// Foreign SDK tags already waited on without result; never waited on twice.
const abandoned = new WeakSet();

function trapGetter () {
  return undefined;
}

function trapSetter (sdk) {
  privateSDK = sdk;
}

function installTrap () {
  if (Object.getOwnPropertyDescriptor(window, SDK_GLOBAL)?.get !== trapGetter) {
    Object.defineProperty(window, SDK_GLOBAL, { configurable: true, get: trapGetter, set: trapSetter });
  }
}

function published () {
  return privateSDK || window[SDK_GLOBAL] || null;
}

/**
 * @param {string} src - Full SDK script url, including the channel id.
 * @param {object} options
 *        {number} options.timeoutMs - How long to wait for the SDK to become ready.
 * @returns {Promise<object>} Resolves with the SDK.
 */
export function loadLeadsSDK (src, options = {}) {
  const { timeoutMs = READY_TIMEOUT_MS } = options;

  if (published()) {
    return Promise.resolve(published());
  }

  if (pending) {
    return pending;
  }

  const ours = injected?.isConnected ? injected : null;
  const foreign = ours
    ? null
    : Array.from(document.querySelectorAll(SDK_SCRIPT_SELECTOR)).find((s) => s !== injected && !abandoned.has(s));

  if (!ours && !foreign && !src) {
    return Promise.reject(new Error('Leads SDK script src is not configured'));
  }

  pending = new Promise((resolve, reject) => {
    let settled = false;
    let pollTimer;

    // Covers a request that never settles as well as an init that never publishes.
    const deadline = setTimeout(() => fail(new Error('Leads SDK did not become ready')), timeoutMs);

    function fail (error) {
      if (settled) {
        return;
      }

      settled = true;
      clearTimeout(deadline);
      clearTimeout(pollTimer);

      if (foreign) {
        abandoned.add(foreign);
      }

      pending = null;
      failPending = null;
      reject(error);
    }

    function poll () {
      if (settled) {
        return;
      }

      const sdk = published();

      if (sdk) {
        settled = true;
        clearTimeout(deadline);
        pending = null;
        failPending = null;
        resolve(sdk);
      } else {
        pollTimer = setTimeout(poll, POLL_INTERVAL_MS);
      }
    }

    failPending = fail;

    if (!ours && !foreign) {
      const script = document.createElement('script');

      installTrap();
      script.src = src;
      script.async = true;
      script.setAttribute(SCRIPT_ATTR, '');
      script.onerror = () => {
        // A tag that errored will never run; drop it so the next call re-injects.
        script.remove();

        // A stale tag's late error must not fail a wait on a newer, healthy one.
        if (injected === script) {
          injected = null;
          failPending?.(new Error('Leads SDK failed to load'));
        }
      };
      injected = script;
      document.body.appendChild(script);
    }

    poll();
  });

  return pending;
}

/**
 * Reset module state. Tests only.
 */
export function resetLeadsSDKLoader () {
  pending = null;
  privateSDK = null;
  injected = null;
  failPending = null;
  delete window[SDK_GLOBAL];
  document.querySelectorAll(SDK_SCRIPT_SELECTOR).forEach((s) => s.remove());
}
